import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { axiosInstance, useUtilities } from "helpers";
import {
  addTemplatePluginList,
  getAllTemplatePluginList,
  deleteTemplatePluginList,
  updateTemplatePluginList,
} from "redux/reducers/templateLibrary/pluginList";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export async function getPluginList() {
  return axiosInstance
    .get("allPluginslist", {
      params: {
        api_key: localStorage.getItem("apikey"),
      },
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useGetPluginList() {
  return useQuery(["allPluginslist"], getPluginList);
}

export function usePluginList() {
  const { data: pluginListData, isLoading, error } = useGetPluginList();
  const {
    pluginList: { loaded, pluginList },
  } = useUtilities();
  const { dispatch } = useUtilities();
  useEffect(() => {
    if (isLoading || error || loaded || !pluginListData) return;
    dispatch(getAllTemplatePluginList(pluginListData.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pluginListData, isLoading, error, loaded]);

  return { data: pluginList, isLoading, error };
}

export async function addPluginList({ list_name, plugin_list, tags }) {
  return axiosInstance
    .post("addpluginlist", {
      api_key: localStorage.getItem("apikey"),
      list_name,
      plugin_list,
      tags,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useAddPluginList(redirect = true) {
  const { notification, dispatch, navigate } = useUtilities();
  return useMutation(addPluginList, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        dispatch(addTemplatePluginList(data.data));
        notification({
          type: "success",
          message: t("libraryTemplate.pluginList.api.success.added"),
        });
        if (redirect) {
          navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/plugin-lists/${data.data.id}`);
        }
      } else {
        notification({
          type: "error",
          message: t("libraryTemplate.pluginList.api.error.failToAdd"),
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: t("libraryTemplate.pluginList.api.error.failToAdd"),
      });
    },
  });
}

export async function deletePluginList(id) {
  return axiosInstance
    .post("delpluginlist", {
      api_key: localStorage.getItem("apikey"),
      pluginlist_id: id,
    })
    .then((res) => ({ ...res.data, id }))
    .catch((err) => new Error(err));
}

export function useDeletePluginList(redirect = true) {
  const { notification, dispatch, navigate } = useUtilities();
  return useMutation(deletePluginList, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        if (redirect) {
          navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/plugin-lists`);
        }
        notification({
          type: "success",
          message: t("libraryTemplate.pluginList.api.success.deleted"),
        });
        dispatch(deleteTemplatePluginList(data.id));
      } else {
        notification({
          type: "error",
          message: data.message,
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: t("libraryTemplate.pluginList.api.error.failToDelete"),
      });
    },
  });
}

export async function updatePluginList({
  pluginlist_id,
  list_name,
  plugin_list,
  tags,
}) {
  return axiosInstance
    .post("updatepluginlist", {
      api_key: localStorage.getItem("apikey"),
      pluginlist_id,
      list_name,
      plugin_list,
      tags,
    })
    .then((res) => res.data)
    .catch((err) => new Error(err));
}

export function useUpdatePluginList() {
  const { notification, dispatch } = useUtilities();

  return useMutation(updatePluginList, {
    onSuccess: (data) => {
      if (data.status_code === 200) {
        notification({
          type: "success",
          message: t("libraryTemplate.pluginList.api.success.updated"),
        });
        dispatch(updateTemplatePluginList(data.data));
      } else {
        notification({
          type: "error",
          message: t("libraryTemplate.pluginList.api.error.failToUpdate"),
        });
      }
    },
    onError: (error) => {
      notification({
        type: "error",
        message: t("libraryTemplate.pluginList.api.error.failToUpdate"),
      });
    },
  });
}
